import React, { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setState } from "@/store/globalSlice"
import { socket } from "@/utils/utils"
import { QUICK_JOIN_MIN_PLAYERS_FOR_BOT_FILL } from "@mono/constants"
import { SAFE_OVERLAY_PADDING } from "@/utils/safeArea"

interface QuickJoinStatusProps {}

// Below this the countdown starts flashing, so the last stretch of the wait
// reads as "about to happen" rather than as more of the same.
const COUNTDOWN_URGENT_SECONDS = 10

const formatCountdown = (seconds: number) => {
  const safeSeconds = Math.max(Math.ceil(seconds), 0)
  const minutes = Math.floor(safeSeconds / 60)
  return `${minutes}:${String(safeSeconds % 60).padStart(2, "0")}`
}

const QuickJoinStatus: React.FC<QuickJoinStatusProps> = () => {
  const dispatch = useAppDispatch()
  const quickJoinStatus = useAppSelector(
    (state) => state.global.quickJoinStatus
  )
  const [secondsLeft, setSecondsLeft] = useState(0)

  const waitedTime = quickJoinStatus?.waitedTime
  const botFillWaitTime = quickJoinStatus?.botFillWaitTime
  const queueStartedAt = quickJoinStatus?.queueStartedAt

  // The server owns the queue clock, so anchor the countdown on the wait it
  // reports and tick locally from there. Using its queueStartedAt timestamp
  // directly would put a badly set local clock in charge of the countdown — it
  // is only a dependency here, to tell one countdown from the next. The queue
  // restarts its clock whenever it drops below the bot-fill threshold and
  // refills, and both of those reports carry waitedTime 0, so without it the
  // effect would skip the restart and keep counting down to the old deadline.
  useEffect(() => {
    if (waitedTime === undefined || botFillWaitTime === undefined) return
    const deadline = Date.now() + (botFillWaitTime - waitedTime) * 1000
    const tick = () => setSecondsLeft((deadline - Date.now()) / 1000)
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [waitedTime, botFillWaitTime, queueStartedAt])

  if (!quickJoinStatus?.isInQueue) return null

  const handleLeaveQueue = () => {
    dispatch(setState({ disableQuickJoinLocally: false }))
    socket.emit("lobby.leaveQuickJoinQueue")
  }

  const playersNeeded =
    quickJoinStatus.maxPlayers - quickJoinStatus.currentPlayers
  const progressPercent =
    (quickJoinStatus.currentPlayers / quickJoinStatus.maxPlayers) * 100

  const playerSlots = Array.from({ length: quickJoinStatus.maxPlayers })

  // Seats the server is about to hand to bots. Non-zero only once the fill has
  // been announced, a few seconds before the game starts.
  const botsFilling = quickJoinStatus.botsFilling
  const isFillingWithBots = botsFilling > 0
  // Bots only step in once enough humans are waiting, so until then the
  // countdown is meaningless and we keep showing the plain "waiting" copy.
  const showBotFillCountdown =
    !isFillingWithBots &&
    quickJoinStatus.currentPlayers >= QUICK_JOIN_MIN_PLAYERS_FOR_BOT_FILL
  const isCountdownUrgent = secondsLeft <= COUNTDOWN_URGENT_SECONDS
  // Fraction of the bot-fill wait still to run, for the bar under the clock.
  const countdownPercent = botFillWaitTime
    ? Math.min(Math.max((secondsLeft / botFillWaitTime) * 100, 0), 100)
    : 0
  // Every seat taken by humans — the game is starting on its own.
  const isReadyToStart = !isFillingWithBots && playersNeeded === 0

  return (
    <div className={`qj-overlay-in fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md ${SAFE_OVERLAY_PADDING}`}>
      <div
        className="qj-card-in relative w-full max-w-2xl overflow-hidden rounded-3xl border border-yellow-500/20 bg-gradient-to-b from-gray-900 via-gray-950 to-black shadow-[0_30px_100px_rgba(0,0,0,0.7),0_0_90px_rgba(234,179,8,0.1)]"
        data-testid="lobby.quick-join-status"
      >
        {/* Ambient light behind the content: two slow amber glows and a swept
            hairline along the top edge. Purely decorative. */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="qj-orb absolute -left-16 -top-20 h-56 w-56 rounded-full bg-amber-500/20 blur-[70px]" />
          <div
            className="qj-orb absolute -bottom-24 -right-12 h-64 w-64 rounded-full bg-yellow-400/10 blur-[80px]"
            style={{ animationDelay: "3s" }}
          />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-500/30 to-transparent" />
          <div className="qj-hairline-sweep absolute left-1/2 top-0 h-px w-40 -translate-x-1/2 bg-gradient-to-r from-transparent via-yellow-200 to-transparent" />
        </div>

        <div className="relative max-h-[92vh] overflow-y-auto">
          {/* Header */}
          <div className="flex flex-col items-center px-10 pb-7 pt-12 sm:px-6 sm:pb-5 sm:pt-8">
            <div className="relative mb-6 sm:mb-4">
              <div className="qj-ring absolute -inset-1.5 rounded-[1.75rem]" />
              <div className="relative flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-yellow-300 via-amber-500 to-amber-700 shadow-[0_12px_32px_rgba(234,179,8,0.35)] ring-1 ring-yellow-200/40 sm:h-16 sm:w-16">
                <i className="fas fa-bolt text-3xl text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] sm:text-2xl" />
              </div>
            </div>

            <h2 className="font-fourth text-4xl font-bold tracking-wide text-white sm:text-3xl">
              Quick Join
            </h2>

            <div
              className={`mt-4 inline-flex items-center gap-3 rounded-full border px-5 py-2 sm:mt-3 sm:py-1.5 ${
                isFillingWithBots
                  ? "border-yellow-400/40 bg-yellow-400/10"
                  : "border-emerald-400/30 bg-emerald-400/10"
              }`}
            >
              <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                <span
                  className={`qj-radar absolute inset-0 rounded-full ${
                    isFillingWithBots ? "bg-yellow-400" : "bg-emerald-400"
                  }`}
                />
                <span
                  className={`relative h-2.5 w-2.5 rounded-full ${
                    isFillingWithBots ? "bg-yellow-300" : "bg-emerald-400"
                  }`}
                />
              </span>
              <span
                className={`text-sm font-bold uppercase tracking-[0.18em] ${
                  isFillingWithBots ? "text-yellow-200" : "text-emerald-300"
                }`}
              >
                {isFillingWithBots ? "Starting game" : "Searching for players"}
              </span>
              {!isFillingWithBots && (
                <span className="flex items-end gap-0.5 pb-1">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="qj-dot h-1 w-1 rounded-full bg-emerald-300"
                      style={{ animationDelay: `${i * 0.16}s` }}
                    />
                  ))}
                </span>
              )}
            </div>
          </div>

          {/* Seats */}
          <div className="px-10 pb-7 sm:px-6 sm:pb-5">
            <div className="flex flex-wrap items-start justify-center gap-5 sm:gap-3">
              {playerSlots.map((_, i) => {
                const isFilled = i < quickJoinStatus.currentPlayers
                const isBot = !isFilled && isFillingWithBots
                // First open seat while we are still hunting for humans.
                const isNext =
                  !isFilled && !isBot && i === quickJoinStatus.currentPlayers
                return (
                  <div
                    key={i}
                    className="qj-seat-in flex flex-col items-center gap-2.5"
                    style={{ animationDelay: `${i * 70}ms` }}
                  >
                    <div
                      className={`flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl border-2 transition-all duration-500 sm:h-14 sm:w-14 ${
                        isFilled
                          ? "qj-seat-filled scale-105 border-yellow-300/60 bg-gradient-to-br from-yellow-300 via-amber-500 to-amber-700"
                          : isBot
                            ? "qj-seat-bot scale-105 border-yellow-400/40 bg-gradient-to-br from-gray-600 to-gray-800 shadow-lg shadow-black/40"
                            : isNext
                              ? "qj-seat-next border-dashed border-white/10 bg-white/5"
                              : "border-dashed border-white/10 bg-white/5"
                      }`}
                    >
                      <i
                        className={`fas text-2xl transition-all duration-500 sm:text-xl ${
                          isFilled
                            ? "fa-user text-white drop-shadow-[0_2px_3px_rgba(0,0,0,0.35)]"
                            : isBot
                              ? "qj-bot-wobble fa-robot text-yellow-300"
                              : "fa-user text-white/20"
                        }`}
                      />
                    </div>
                    <span
                      className={`text-xs font-bold uppercase tracking-wider transition-colors duration-300 ${
                        isFilled
                          ? "text-yellow-400"
                          : isBot
                            ? "text-yellow-300/70"
                            : isNext
                              ? "text-white/50"
                              : "text-white/30"
                      }`}
                    >
                      {isFilled
                        ? "Joined"
                        : isBot
                          ? "Bot"
                          : isNext
                            ? "Searching"
                            : "Open"}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Progress */}
          <div className="px-10 pb-7 sm:px-6 sm:pb-5">
            <div className="mb-2.5 flex items-end justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-white/40">
                Table filling up
              </span>
              <span className="font-fourth text-lg font-bold tabular-nums text-white">
                {quickJoinStatus.currentPlayers}
                <span className="text-white/40">
                  {" "}
                  / {quickJoinStatus.maxPlayers}
                </span>
              </span>
            </div>
            <div className="h-3.5 w-full overflow-hidden rounded-full bg-white/10 ring-1 ring-inset ring-white/5">
              <div
                className="relative h-full overflow-hidden rounded-full bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-400 shadow-[0_0_16px_rgba(234,179,8,0.5)] transition-all duration-700 ease-out"
                style={{ width: `${progressPercent}%` }}
              >
                {!isReadyToStart && (
                  <span className="qj-progress-stripes absolute inset-0" />
                )}
              </div>
            </div>
          </div>

          {/* Headline status — the one line that has to be readable at a
              glance, so it gets the largest type on the card. */}
          <div className="px-10 pb-8 sm:px-6 sm:pb-6">
            {isFillingWithBots ? (
              <div
                className="qj-banner-alert relative overflow-hidden rounded-2xl border border-yellow-500/40 bg-gradient-to-r from-yellow-500/20 via-yellow-500/10 to-transparent px-6 py-5 sm:px-4 sm:py-4"
                data-testid="lobby.quick-join-bot-fill-notice"
              >
                <span className="qj-banner-sheen pointer-events-none absolute inset-0" />
                <div className="relative flex items-center gap-5 sm:gap-4">
                  <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-yellow-500/20 ring-1 ring-yellow-300/30 sm:h-11 sm:w-11">
                    <i className="qj-bot-wobble fas fa-robot text-2xl text-yellow-300 sm:text-xl" />
                  </span>
                  <div className="flex-1">
                    <p className="font-fourth text-2xl font-extrabold leading-tight tracking-wide text-yellow-100 sm:text-xl">
                      Filling{" "}
                      <span className="text-yellow-300">{botsFilling}</span>{" "}
                      {botsFilling > 1 ? "seats" : "seat"} with{" "}
                      {botsFilling > 1 ? "bots" : "a bot"}
                    </p>
                    <p className="mt-1.5 text-base text-yellow-100/60 sm:text-sm">
                      No more players joined, so the game is starting now.
                    </p>
                  </div>
                </div>
              </div>
            ) : isReadyToStart ? (
              <div className="qj-ready-pop relative overflow-hidden rounded-2xl border border-emerald-400/40 bg-gradient-to-r from-emerald-500/20 via-emerald-500/10 to-transparent px-6 py-5 sm:px-4 sm:py-4">
                <span className="qj-banner-sheen pointer-events-none absolute inset-0" />
                <div className="relative flex items-center gap-5 sm:gap-4">
                  <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-emerald-500/20 ring-1 ring-emerald-300/30 sm:h-11 sm:w-11">
                    <i className="fas fa-rocket text-2xl text-emerald-300 sm:text-xl" />
                  </span>
                  <div className="flex-1">
                    <p className="font-fourth text-2xl font-extrabold leading-tight tracking-wide text-emerald-100 sm:text-xl">
                      Table is full — starting now
                    </p>
                    <p className="mt-1.5 text-base text-emerald-100/60 sm:text-sm">
                      Loading the board, hang tight.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="qj-banner-glow relative overflow-hidden rounded-2xl border border-yellow-500/20 bg-gradient-to-r from-yellow-500/10 via-yellow-500/5 to-transparent px-6 py-5 sm:px-4 sm:py-4">
                <span className="qj-banner-sheen pointer-events-none absolute inset-0" />
                <div className="relative flex items-center gap-5 sm:gap-4">
                  <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-yellow-500/10 ring-1 ring-yellow-300/20 sm:h-11 sm:w-11">
                    <i className="fas fa-user-group text-2xl text-yellow-300 sm:text-xl" />
                  </span>
                  <div className="flex-1">
                    <p className="font-fourth text-2xl font-extrabold leading-tight tracking-wide text-white sm:text-xl">
                      Waiting for{" "}
                      <span className="text-yellow-300">{playersNeeded}</span>{" "}
                      more {playersNeeded > 1 ? "players" : "player"}
                    </p>
                    <p className="mt-1.5 text-base text-white/50 sm:text-sm">
                      You drop straight into the game the moment the table
                      fills.
                    </p>
                  </div>
                </div>

                {showBotFillCountdown && (
                  <div
                    className="relative mt-5 rounded-xl border border-yellow-400/20 bg-black/40 px-5 py-4 sm:mt-4 sm:px-4 sm:py-3"
                    data-testid="lobby.quick-join-bot-fill-countdown"
                  >
                    <div className="flex items-center gap-4">
                      <i className="qj-bot-wobble fas fa-robot text-xl text-yellow-300 sm:text-base" />
                      <span className="flex-1 text-base font-semibold leading-snug text-yellow-100/80 sm:text-sm">
                        Filling empty {playersNeeded > 1 ? "seats" : "seat"}{" "}
                        with bots in
                      </span>
                      <span
                        className={`font-fourth text-3xl font-extrabold tabular-nums text-yellow-300 sm:text-2xl ${
                          isCountdownUrgent ? "qj-countdown-urgent" : ""
                        }`}
                      >
                        {formatCountdown(secondsLeft)}
                      </span>
                    </div>
                    {/* Draining bar so the remaining wait is readable without
                        parsing the digits. */}
                    <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-white/10">
                      <div
                        className={`h-full rounded-full transition-[width] duration-1000 ease-linear ${
                          isCountdownUrgent ? "bg-yellow-200" : "bg-yellow-400/60"
                        }`}
                        style={{ width: `${countdownPercent}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Leave */}
          {!isFillingWithBots && (
            <div className="px-10 pb-10 sm:px-6 sm:pb-7">
              <button
                onClick={handleLeaveQueue}
                className="group flex w-full items-center justify-center gap-3 rounded-xl border border-white/10 bg-white/5 py-4 font-fourth text-lg font-bold tracking-wide text-white/70 transition-all duration-200 hover:border-red-500/40 hover:bg-red-600/80 hover:text-white hover:shadow-lg hover:shadow-red-500/20 active:scale-[0.98] sm:py-3 sm:text-base"
                data-testid="lobby.leave-quick-join-btn"
              >
                <i className="fas fa-arrow-right-from-bracket text-base transition-transform duration-200 group-hover:-translate-x-0.5" />
                Leave Queue
              </button>
            </div>
          )}
          {isFillingWithBots && <div className="pb-10 sm:pb-7" />}
        </div>
      </div>
    </div>
  )
}

export default QuickJoinStatus
